import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { IconMenu } from '../components/Icons';
import CustomerSidebar from '../components/CustomerSidebar';
import CustomerSiteMap from './customer/CustomerSiteMap';
import CustomerAlertsList from './customer/CustomerAlertsList';
import GateGroupsPage, { GateGroupDetailPage } from './shared/GateGroupsPage';
import UsersPage from './shared/UsersPage';
import UserProfile from './shared/UserProfile';
import SettingsPage from './customer/SettingsPage';
import '../business-mobile.css';

/**
 * Phase 3.F — Customer App shell with persistent sidebar.
 *
 * Layout split:
 *   - Desktop (>= 768px): permanent right rail (RTL "start") with the
 *     sidebar; main content fills the rest.
 *   - Mobile (< 768px): sidebar hides; a slim top bar exposes a
 *     hamburger that opens the sidebar as a drawer.
 *
 * The unread-alert count is fetched here so a single counter feeds
 * both the sidebar badge and the eventual mobile-header bell.
 */
function CustomerHome({ user, setUser }) {
  const [alertCount, setAlertCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const refreshAlertCount = useCallback(() => {
    axios.get('/api/customer/alerts')
      .then(res => {
        const unread = res.data.filter(a => !a.is_read).length;
        setAlertCount(unread);
      })
      .catch(() => {});
  }, []);

  useEffect(refreshAlertCount, [refreshAlertCount]);

  const handleLogout = async () => {
    await axios.post('/api/auth/logout');
    setUser(null);
  };

  const canManageUsers = !!user.permissions?.can_manage_users;
  const isManager = user.role === 'manager';

  return (
    <div className="customer-app-layout">
      <CustomerSidebar
        user={user}
        alertCount={alertCount}
        onLogout={handleLogout}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="customer-app-main">
        <div className="customer-mobile-header">
          <button
            type="button"
            className="customer-mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="פתח תפריט"
          >
            <IconMenu size={22} />
          </button>
          <div className="customer-mobile-title">Huri-Connect</div>
          <div className="customer-mobile-brand" aria-hidden="true">H</div>
        </div>

        <div className="page-content">
          <Routes>
            <Route path="/" element={<SitesOverview />} />
            <Route
              path="/alerts"
              element={<CustomerAlertsList onAfterChange={refreshAlertCount} />}
            />
            <Route path="/sites/:id" element={<CustomerSiteMap user={user} />} />
            <Route path="/sites/:id/groups" element={<GateGroupsPage apiBase="customer" />} />
            <Route path="/sites/:id/groups/:gid" element={<GateGroupDetailPage apiBase="customer" />} />
            {canManageUsers && (
              <Route
                path="/users"
                element={<UsersIndexRoute userId={user.id} />}
              />
            )}
            {canManageUsers && (
              <Route
                path="/users/:userId"
                element={<UserProfileRoute userId={user.id} />}
              />
            )}
            {isManager && <Route path="/settings" element={<SettingsPage />} />}
          </Routes>
        </div>
      </div>
    </div>
  );
}

function UsersIndexRoute({ userId }) {
  const navigate = useNavigate();
  return (
    <UsersPage
      usersBase="/api/customer/users"
      currentUserId={userId}
      onOpenProfile={(u) => navigate(`/users/${u.id}`)}
    />
  );
}


function UserProfileRoute({ userId: currentUserId }) {
  const navigate = useNavigate();
  const { userId } = useParams();
  return (
    <UserProfile
      usersBase="/api/customer/users"
      userId={parseInt(userId, 10)}
      currentUserId={currentUserId}
      onBack={() => navigate('/users')}
    />
  );
}


function SitesOverview() {
  const [sites, setSites] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/customer/sites').then(res => {
      setSites(res.data);
      if (res.data.length === 1) {
        navigate(`/sites/${res.data[0].id}`, { replace: true });
      }
    });
  }, [navigate]);

  return (
    <div className="site-cards">
      {sites.map(s => (
        <div key={s.id} className="site-card" onClick={() => navigate(`/sites/${s.id}`)}>
          <h3>{s.name}</h3>
          <div className="info">{s.address}</div>
          <div className="info">{s.gates_count} שערים</div>
        </div>
      ))}
    </div>
  );
}

export default CustomerHome;
